/**
 * migrate-layout-to-recursive (admin copy) — F12.N E1.T5b.
 *
 * KOPIA 1:1 z apps/api/lib/system-modules/migrate-layout-to-recursive.ts.
 * Używana w hooku `useSystemModuleActiveLayout` żeby zmigrować stary kształt
 * layoutu w locie, jeśli backend zwróci stare dane (skrypt migracji w bazie
 * nie został jeszcze odpalony — patrz E1.T7 i deploy.sh).
 *
 * Po deploy z E1.T7 (skrypt `migrate-layouts-to-recursive-once.ts` odpalony
 * raz przy deployu) wszystkie layouty w bazie będą w nowym kształcie i
 * `isAlreadyNewShape()` zawsze będzie zwracać `true` — czyli ten code path
 * w hooku stanie się no-op. Wtedy ten plik można usunąć (admin) i zostawić
 * jeden źródło prawdy w api.
 *
 * Plik utrzymywać równolegle do source-of-truth w api — żadna logika
 * zmieniona, czysty copy-paste. (Obejmuje też pominięcie zakładki `ext_fields` —
 * „Pola dodatkowe" nie są już zakładką ani blokiem; ta sama zmiana jest w api.)
 */

// ─── Typy starej (płaskiej) struktury ────────────────────────────────────────

interface OldFieldElement {
  id: string;
  type: 'field';
  fieldId: string;
  width?: 1 | 2 | 3;
  settings?: Record<string, unknown>;
}

interface OldSection {
  id: string;
  title: string | null;
  columns: 1 | 2 | 3;
  elements: OldFieldElement[];
}

type OldTabType =
  | 'fields'
  | 'subcollection'
  | 'audit_log'
  | 'ext_fields'
  | 'tree_picker'
  | 'satellite';

interface OldTab {
  id: string;
  slug?: string;
  title: string;
  type: OldTabType;
  hidden?: boolean;
  sortOrder?: number;
  sections?: OldSection[];
  collection?: string;
  visibleRoleIds?: string[];
}

interface OldLayoutConfig {
  tabs?: OldTab[];
  /** Pre-C.7 fallback shape (płaskie sections na root, bez tabs) — bardzo stare layouty. */
  sections?: OldSection[];
}

// ─── Funkcje pomocnicze ──────────────────────────────────────────────────────

/**
 * Wykrywa czy layout_config jest już w NOWYM kształcie (post-F12.N).
 *
 * Nowy kształt ma `elements` na top-level (bez `tabs` na root).
 * Stary kształt ma `tabs` (post-C.7) lub `sections` (pre-C.7) na root.
 */
export function isAlreadyNewShape(layoutConfig: unknown): boolean {
  if (!layoutConfig || typeof layoutConfig !== 'object') return false;
  const cfg = layoutConfig as { elements?: unknown; tabs?: unknown; sections?: unknown };
  return Array.isArray(cfg.elements) && !Array.isArray(cfg.tabs) && !Array.isArray(cfg.sections);
}

type UuidGenerator = () => string;

const defaultUuidGenerator: UuidGenerator = () => crypto.randomUUID();

// ─── Główna funkcja transformacji ────────────────────────────────────────────

export interface MigrateOptions {
  uuid?: UuidGenerator;
}

/**
 * Transformuje stary płaski layout_config na nowy rekurencyjny.
 *
 * Zachowuje wszystkie identyfikatory ze starej struktury — id elementów pól,
 * id sekcji, id zakładek. Nowe id są generowane tylko dla:
 *  - korzennego elementu `type='tabs'`,
 *  - placeholder-element wewnątrz zakładek non-fields.
 *
 * Zakładka starego kształtu `type='ext_fields'` („Pola dodatkowe") jest pomijana (nic z niej
 * nie powstaje) — pole dodatkowe pokazuje się tylko jako zwykłe pole postawione w układzie.
 * Gdy po pominięciu nie zostaje żadna zakładka, wynik jak dla pustego configu
 * (`{ elements: [] }`).
 */
export function migrateLayoutToRecursive(
  oldConfig: unknown,
  options: MigrateOptions = {},
): { elements: unknown[] } {
  const uuid = options.uuid ?? defaultUuidGenerator;

  if (!oldConfig || typeof oldConfig !== 'object') {
    return { elements: [] };
  }

  const cfg = oldConfig as OldLayoutConfig;

  let oldTabs: OldTab[];
  if (Array.isArray(cfg.tabs) && cfg.tabs.length > 0) {
    oldTabs = cfg.tabs;
  } else if (Array.isArray(cfg.sections) && cfg.sections.length > 0) {
    oldTabs = [
      {
        id: uuid(),
        slug: 'data',
        title: 'Dane',
        type: 'fields',
        hidden: false,
        sortOrder: 0,
        sections: cfg.sections,
      },
    ];
  } else {
    return { elements: [] };
  }

  // Stara zakładka „Pola dodatkowe" (type='ext_fields') nie jest odtwarzana — nie ma już takiego
  // bloku w żadnym module. Filtrujemy PRZED mapowaniem, żeby indeks zakładki (domyślny slug
  // `tab-<idx>` i `sortOrder`) liczył się po pominięciu. Gdy nic nie zostaje — pusty nowy layout
  // (jak dla pustego `tabs[]`).
  oldTabs = oldTabs.filter((oldTab) => oldTab.type !== 'ext_fields');
  if (oldTabs.length === 0) {
    return { elements: [] };
  }

  const newTabs = oldTabs.map((oldTab, idx) => {
    const children = buildTabChildrenFromOldTab(oldTab, uuid);
    return {
      id: oldTab.id,
      slug: oldTab.slug ?? `tab-${idx}`,
      title: oldTab.title,
      hidden: oldTab.hidden ?? false,
      sortOrder: oldTab.sortOrder ?? idx,
      ...(oldTab.visibleRoleIds ? { visibleRoleIds: oldTab.visibleRoleIds } : {}),
      children,
    };
  });

  return {
    elements: [
      {
        id: uuid(),
        type: 'tabs',
        tabs: newTabs,
      },
    ],
  };
}

function buildTabChildrenFromOldTab(oldTab: OldTab, uuid: UuidGenerator): unknown[] {
  switch (oldTab.type) {
    case 'fields': {
      const sections = oldTab.sections ?? [];
      return sections.map((sec) => ({
        id: sec.id,
        type: 'section',
        settings: {
          title: sec.title,
          columns: sec.columns,
        },
        children: sec.elements.map((el) => ({
          id: el.id,
          type: el.type,
          fieldId: el.fieldId,
          ...(el.settings ? { settings: el.settings } : {}),
          ...(el.width !== undefined ? { width: el.width } : {}),
        })),
      }));
    }

    case 'subcollection':
      return [
        {
          id: uuid(),
          type: 'related_records_table',
          settings: oldTab.collection ? { collection: oldTab.collection } : {},
        },
      ];

    case 'audit_log':
      return [
        {
          id: uuid(),
          type: 'audit_log_view',
          settings: {},
        },
      ];

    // 'ext_fields' nie ma gałęzi: zakładki tego typu są odfiltrowane w `migrateLayoutToRecursive`
    // przed mapowaniem (patrz komentarz tam).

    case 'tree_picker':
      return [
        {
          id: uuid(),
          type: 'tree_picker_view',
          settings: oldTab.collection ? { collection: oldTab.collection } : {},
        },
      ];

    case 'satellite':
      return [
        {
          id: uuid(),
          type: 'satellite_view',
          settings: {},
        },
      ];

    default: {
      const unknownType = (oldTab as { type: string }).type;
      return [
        {
          id: uuid(),
          type: 'unknown_legacy_tab_content',
          settings: { originalType: unknownType },
        },
      ];
    }
  }
}
